<template>
    <form-wrapper @submit="handleFormSubmission(data)">
        <form-row>
            <form-cell width="half">
                <text-field
                    :class="invalidNpiInputClass"
                    :label="$t('forms.auth.profile.fields.npi_number.label')"
                    :placeholder="$t('forms.auth.profile.fields.npi_number.placeholder')"
                    :isRequired="true"
                    :hasAutofocus="true"
                    :formData="data"
                    valuePath="npiNumber"
                    @change="change($event)"
                ></text-field>
            </form-cell>
        </form-row>

        <form-row>
            <common-page-header
                :heading="$t('pages.profile.professional.heading.specialities')"
                size="page-plain-header"
            ></common-page-header>
        </form-row>

        <form-row align="bottom">
            <form-cell width="half">
                <form-info-field :text="specialityName" :showDeleteButton="false"></form-info-field>
            </form-cell>
            <form-cell width="half">
                <select-field
                    :label="$t('forms.auth.register.fields.physicianGroup.label')"
                    :placeholder="$t('forms.auth.register.fields.physicianGroup.placeholder')"
                    :options="physicianGroupsExtended"
                    :isRequired="false"
                    :formData="data"
                    valuePath="physicianGroupId"
                    @change="change($event)"
                ></select-field>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell>
                <multi-select-field
                    :class="invalidSpecialitiesInputClass"
                    :label="$t('forms.professional_info.sub_specialities.label')"
                    :isRequired="true"
                    :formData="data"
                    :options="specialities"
                    :expandableOptions="true"
                    :oneOptionSelected="true"
                    valuePath="specialities"
                    @change="change($event)"
                ></multi-select-field>
            </form-cell>
            <form-cell>
                <year-field
                    :class="invalidPracticingSinceInputClass"
                    :label="$t('forms.auth.referrals.fields.practicing_since.label')"
                    :placeholder="$t('forms.auth.referrals.fields.practicing_since.placeholder')"
                    :isRequired="true"
                    :formData="data"
                    valuePath="practicingSince"
                    @change="change($event)"
                ></year-field>
            </form-cell>
        </form-row>

        <form-row>
            <common-page-header
                :heading="$t('pages.profile.professional.heading.licences')"
                size="page-plain-header"
            ></common-page-header>
        </form-row>
        <licenses-fragment
            :states="states"
            :formData="data"
            :showHint="false"
            format="compact-non-mandatory"
            valuePath="medicalLicenses"
            @change="change($event)"
        ></licenses-fragment>

        <form-row align="bottom">
            <div class="form-row-cell">
                <form-cell>
                    <number-field
                        class="form-field__fix_length"
                        :label="$t('forms.auth.referrals.fields.disciplinary_actions.label')"
                        :isRequired="true"
                        :formData="data"
                        valuePath="disciplinaryActions"
                        @change="change($event)"
                    ></number-field>
                </form-cell>
                <form-cell>
                    <number-field
                        class="form-field__fix_length"
                        :label="$t('forms.auth.referrals.fields.malpractice_claims_lost.label')"
                        :isRequired="true"
                        :formData="data"
                        :hint="$t('forms.auth.referrals.fields.malpractice_claims_lost.hint')"
                        valuePath="malpracticeClaimsLost"
                        @change="change($event)"
                    ></number-field>
                </form-cell>
                <form-cell>
                    <boolean-field
                        :label="$t('forms.auth.referrals.fields.board_certified.label')"
                        :isRequired="true"
                        :formData="data"
                        valuePath="boardCertified"
                        @change="change($event)"
                    ></boolean-field>
                </form-cell>
            </div>
        </form-row>

        <form-row>
            <common-page-header
                :heading="$t('pages.profile.professional.heading.short_biography')"
                size="page-plain-header"
            ></common-page-header>
        </form-row>
        <form-row>
            <form-cell>
                <textarea-field
                    :label="$t('forms.professional_info.short_biography.label')"
                    :placeholder="$t('forms.professional_info.short_biography.placeholder')"
                    :isRequired="false"
                    :formData="data"
                    valuePath="shortBiography"
                    @change="change($event)"
                ></textarea-field>
            </form-cell>
        </form-row>

        <form-row>
            <common-page-header
                :heading="$t('pages.profile.professional.heading.medical_schools')"
                size="page-plain-header"
                contentSpacing="medium"
            ></common-page-header>
        </form-row>
        <professional-info-fragment
            :states="states"
            :formData="data"
            :showRemoveProfessionalInfoModal="true"
            propertyName="medical school"
            translationPropertyName="medical_schools"
            valuePath="medicalSchools"
            @change="change($event)"
        ></professional-info-fragment>

        <form-row>
            <common-page-header
                :heading="$t('pages.profile.professional.heading.affiliated_hospitals')"
                size="page-plain-header"
                contentSpacing="medium"
            ></common-page-header>
        </form-row>
        <professional-info-fragment
            :states="states"
            :formData="data"
            :showRemoveProfessionalInfoModal="true"
            propertyName="affiliated hospital"
            translationPropertyName="affiliated_hospitals"
            valuePath="affiliatedHospitals"
            @change="change($event)"
        ></professional-info-fragment>

        <form-row>
            <common-page-header
                :heading="$t('pages.profile.professional.heading.residencies')"
                size="page-plain-header"
                contentSpacing="medium"
            ></common-page-header>
        </form-row>
        <professional-info-fragment
            :states="states"
            :formData="data"
            :showRemoveProfessionalInfoModal="true"
            propertyName="residency"
            translationPropertyName="residencies"
            valuePath="residencies"
            @change="change($event)"
        ></professional-info-fragment>

        <form-row>
            <common-page-header
                :heading="$t('pages.profile.professional.heading.fellowships')"
                size="page-plain-header"
                contentSpacing="medium"
            ></common-page-header>
        </form-row>
        <professional-info-fragment
            :states="states"
            :formData="data"
            :showRemoveProfessionalInfoModal="true"
            propertyName="fellowship"
            translationPropertyName="fellowships"
            valuePath="fellowships"
            @change="change($event)"
        ></professional-info-fragment>

        <template #footer>
            <common-buttons>
                <common-button
                    :isSubmittable="false"
                    :label="$t('pages.profile.professional.buttons.secondary')"
                    type="secondary"
                    size="large"
                    @click="handleCloseEvent()"
                ></common-button>
                <common-button
                    :isProcessing="isSending"
                    :isDisabled="isSubmitButtonDisabled()"
                    :label="$t('pages.profile.professional.buttons.primary')"
                    size="large"
                    type="primary"
                ></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import UsersAPI from 'api/UsersAPI'
    import FormMixin from 'mixins/FormMixin'
    import HandleRequestMixin from 'mixins/HandleRequestMixin'

    export default {
        mixins: [FormMixin, HandleRequestMixin],

        props: {
            states: { type: Array, required: true },
            specialities: { type: Array, required: true },
            physicianGroups: { type: Array, required: true },
        },

        data() {
            return {
                userId: null,
                specialityName: '',
            }
        },

        computed: {
            invalidNpiInputClass() {
                return { 'form-input-text--is-invalid': !this.data.npiNumber }
            },

            invalidSpecialitiesInputClass() {
                return { 'form-input-select--is-invalid': !this.data.specialities || this.data.specialities.length === 0 }
            },

            invalidPracticingSinceInputClass() {
                return { 'form-input-select--is-invalid': !this.data.practicingSince }
            },

            specialityLabel() {
                const subspecialityId = this.data.specialities[0]
                const speciality = this.specialities.find((speciality) => {
                    return (
                        speciality.options && speciality.options.find((subspeciality) => subspeciality.value === subspecialityId)
                    )
                })
                if (speciality && speciality.label) {
                    return speciality.label
                }
                return ''
            },

            physicianGroupsExtended() {
                return [{ value: -1, label: 'None' }, ...this.physicianGroups]
            },
        },

        methods: {
            isSubmitButtonDisabled() {
                return !(
                    this.data.npiNumber &&
                    this.data.specialities &&
                    this.data.specialities.length > 0 &&
                    this.data.practicingSince
                )
            },

            handleFormSubmission(data) {
                const selectedSpecialities = this.getSpecialitiesDataForSubmission(data.specialities)
                const {
                    npiNumber,
                    disciplinaryActions,
                    malpracticeClaimsLost,
                    boardCertified,
                    practicingSince,
                    shortBiography,
                    physicianGroupId,
                } = data

                const dataToSubmit = {
                    npiNumber,
                    disciplinaryActions,
                    malpracticeClaimsLost,
                    boardCertified,
                    practicingSince,
                    shortBiography,
                    physicianGroupId: physicianGroupId !== -1 ? physicianGroupId : null,
                    medicalLicenses: this.getArrayDataForSubmission(data.medicalLicenses),
                    medicalSchools: this.getArrayDataForSubmission(data.medicalSchools),
                    affiliatedHospitals: this.getArrayDataForSubmission(data.affiliatedHospitals),
                    residencies: this.getArrayDataForSubmission(data.residencies),
                    fellowships: this.getArrayDataForSubmission(data.fellowships),
                    userSpecialities: selectedSpecialities,
                }

                this.updateUser(this.userId, dataToSubmit)
            },

            updateUser(userId, data) {
                this.handleRequest(UsersAPI.update(userId, data))
                    .then(() => {
                        this.$router.push({ name: 'index.home' })
                    })
                    .catch(() => {})
            },

            reloadData() {
                const physician = this.$auth.getActiveMember()

                this.userId = physician.id
                this.specialityName = physician.specialities[0].speciality.name

                const specialitiesSelection = physician.specialities.reduce((acc, value) => {
                    if (!acc.includes(value.subSpeciality.id)) {
                        acc.push(value.subSpeciality.id)
                    }
                    return acc
                }, [])

                const { npiNumber, shortBiography, physicianGroupId } = physician

                const dataToPrefill = {
                    npiNumber,
                    physicianGroupId: physicianGroupId ? physicianGroupId : -1,
                    medicalSchools: this.getArrayDataToPrefill(physician.medicalSchools),
                    affiliatedHospitals: this.getArrayDataToPrefill(physician.affiliatedHospitals),
                    residencies: this.getArrayDataToPrefill(physician.residencies),
                    fellowships: this.getArrayDataToPrefill(physician.fellowships),
                    shortBiography,
                    medicalLicenses: this.getArrayDataToPrefill(physician.medicalLicenses),
                    specialities: specialitiesSelection,
                    practicingSince: physician.specialities[0].practicingSince,
                    disciplinaryActions: physician.disciplinaryActions || 0,
                    malpracticeClaimsLost: physician.malpracticeClaimsLost || 0,
                    boardCertified: physician.boardCertified || false,
                }
                this.prefillData(dataToPrefill)
            },

            handleCloseEvent() {
                this.$router.push({ name: 'index.home' })
            },

            getArrayDataForSubmission(array) {
                if (array && array.length) {
                    return array.filter((value) => Object.keys(value).length !== 0)
                }
                return []
            },

            getArrayDataToPrefill(array) {
                if (!array || !array.length) {
                    return [{}]
                }
                return array
            },

            getSpecialitiesDataForSubmission(specialitiesData) {
                return this.specialities
                    .flatMap((spec) => {
                        if (spec.options.length === 0) {
                            return spec
                        } else {
                            return spec.options
                        }
                    })
                    .filter((spec) => {
                        if (spec.data) {
                            return specialitiesData.includes(spec.data.id)
                        }
                        return specialitiesData.includes(spec.value)
                    })
                    .reduce((acc, speciality) => {
                        let spec
                        if (speciality.data) {
                            spec = {
                                specialityId: speciality.data.optionId,
                                subSpecialityId: speciality.value,
                            }
                        } else {
                            spec = {
                                specialityId: speciality.value,
                            }
                        }
                        acc.push(spec)
                        return acc
                    }, [])
            },
        },

        mounted() {
            this.reloadData()
            window.scrollTo(0, 0)
        },
    }
</script>
<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';
    .form-row-cell {
        @include breakpoint(smartphone) {
            flex-wrap: wrap;
        }

        display: flex;
        justify-content: space-between;
        width: 100%;
        align-items: flex-end;
        max-width: 37rem;
    }
</style>
